// Request form: every date from today is open, no notice (owner 2026-10-09). The page states today's date as the
// store saw it when rendering (its own time zone, possibly a cached page from yesterday); the earliest date becomes
// the visitor's own today, so today is never closed and yesterday never open.
const visitorToday = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};
for (const input of document.querySelectorAll('input[type="date"][data-min-today]')) {
  input.min = visitorToday();
}

// Booking app mode (Meety): our add-to-cart button stays hidden (workshop.css) and the card says the dates are loading
// until the app's own buttons are in (owner report 2026-10-11). The theme re-renders the product section after load, so
// the card is replaced: everything here works from the document and looks the current card up when it acts.
const bookingAppOnPage = () => Boolean(document.getElementById('meety-app-root') || document.querySelector('template#meety-block-buttons, script[src*="meety"]'));
// The fallback line (refresh or email us). "force" keeps it visible even next to the app's buttons (a booking was
// stopped); otherwise the CSS still hides it once the app's buttons are in.
const showBookingFallback = (card, force) => {
  const status = card?.querySelector('[data-booking-app-status]');
  if (!status) return;
  status.textContent = status.dataset.failed;
  status.hidden = false;
  if (force) status.classList.add('is-shown');
};

if (document.querySelector('[data-booking-app]')) {
  if (!bookingAppOnPage()) {
    // No app on the page at all: our own button comes back, on the card and on any re-rendered one.
    const markMissing = () => document.querySelectorAll('[data-booking-app]:not(.booking-app-missing)').forEach((card) => {
      card.classList.add('booking-app-missing');
      const status = card.querySelector('[data-booking-app-status]');
      if (status) status.hidden = true;
    });
    markMissing();
    new MutationObserver(markMissing).observe(document.body, { childList: true, subtree: true });
  } else {
    // The loading line goes once the app's buttons are in. The CSS does this where :has() exists; this covers the rest
    // and any re-rendered card, and stops watching once every card has them.
    const hideLoadedStatus = () => {
      let waiting = false;
      document.querySelectorAll('[data-booking-app]').forEach((card) => {
        const status = card.querySelector('[data-booking-app-status]');
        if (!status || status.classList.contains('is-shown')) return;
        if (card.querySelector('.meety-block-buttons')) status.hidden = true;
        else waiting = true;
      });
      return waiting;
    };
    if (hideLoadedStatus()) {
      const statusObserver = new MutationObserver(() => { if (!hideLoadedStatus()) statusObserver.disconnect(); });
      statusObserver.observe(document.body, { childList: true, subtree: true });
    }

    // The app shows and hides elements with inline "display: ... !important", which beats any stylesheet: when it has
    // no service for the variant (after the variants were deleted, 2026-10-11) it puts our add-to-cart button back, and
    // a click on it booked with no date. So a booking card's form only goes through once the app has added its booking
    // fields to it (properties[_meety_variantId], when it books through the form); otherwise the card says to refresh
    // or email. Capture on the document runs before the theme's product-form handler.
    document.addEventListener('submit', (event) => {
      const card = event.target.closest?.('[data-booking-app]');
      if (!card || card.classList.contains('booking-app-missing') || event.target.querySelector('[name="properties[_meety_variantId]"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showBookingFallback(card, true);
    }, true);

    // Still no app buttons after 15 s: the loading line becomes the fallback (the CSS hides it once they are in).
    window.setTimeout(() => {
      document.querySelectorAll('[data-booking-app]').forEach((card) => { if (!card.querySelector('.meety-block-buttons')) showBookingFallback(card); });
    }, 15000);
  }
}

// Mobile sticky "Book" bar for the workshop booking card (spec §4.7). It shows once the card has scrolled above the
// viewport; its link is a plain anchor back to the card, where the customer taps the booking app's own button.
//
// It deliberately never calls .click() on the booking app's button: that button sits inside the product form, and a
// synthetic click can submit the form, which adds the variant to the cart without a booking date.
if (!window.customElements.get('workshop-booking-sticky')) {
  window.customElements.define('workshop-booking-sticky', class extends HTMLElement {
    connectedCallback() {
      const card = document.getElementById(this.getAttribute('for'));

      if (!card) {
        return;
      }

      this.observer = new IntersectionObserver(([entry]) => {
        this.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
      this.observer.observe(card);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  });
}

// Group bookings (corporate spec §8.4): the booking card in group mode. The number of people picks the variant whose
// size range contains it (one variant per per-person price tier) and is the cart quantity, so a group is always booked
// at its own tier's price. Tiers come from the card's JSON: [{min, max (null = open), id, cents, price}], in order.
// Outside every range (or before a number is typed) the first tier stays selected and no total is shown; the input's
// min/required attributes stop the form from submitting an invalid number.
if (!window.customElements.get('workshop-group-booking')) {
  window.customElements.define('workshop-group-booking', class extends HTMLElement {
    connectedCallback() {
      this.tiers = JSON.parse(this.querySelector('script[data-group-tiers]')?.textContent || '[]');
      this.sizeInput = this.querySelector('[data-group-size]');
      this.variantInput = this.querySelector('[data-group-variant]');
      this.priceElement = this.querySelector('[data-group-price]');
      this.totalElement = this.querySelector('[data-group-total]');

      if (this.tiers.length === 0 || !this.sizeInput || !this.variantInput) {
        return;
      }

      this.initialPrice = this.priceElement ? this.priceElement.textContent : '';
      this.formatter = this.createTotalFormatter();
      this.onInput = () => this.update();
      this.sizeInput.addEventListener('input', this.onInput);
      this.update();
    }

    disconnectedCallback() {
      this.sizeInput?.removeEventListener('input', this.onInput);
    }

    // "$1,125", like the theme's own prices: under an "en" locale the default symbol for CAD is "CA$". Browsers older
    // than Safari 14.1 / Chrome 77 reject 'narrowSymbol' with a RangeError; they get the default symbol instead, because
    // a throw here would stop the size input from ever picking its tier's variant.
    createTotalFormatter() {
      const locale = this.dataset.locale || undefined;
      const options = {
        style: 'currency',
        currency: this.dataset.currency || 'CAD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      };

      try {
        return new Intl.NumberFormat(locale, { ...options, currencyDisplay: 'narrowSymbol' });
      } catch (error) {
        if (!(error instanceof RangeError)) {
          throw error;
        }

        return new Intl.NumberFormat(locale, options);
      }
    }

    tierFor(count) {
      return this.tiers.find((tier) => count >= tier.min && (tier.max === null || count <= tier.max)) || null;
    }

    update() {
      const count = Number.parseInt(this.sizeInput.value, 10);
      const tier = Number.isInteger(count) && count > 0 ? this.tierFor(count) : null;

      this.variantInput.value = String((tier || this.tiers[0]).id);

      if (this.priceElement) {
        this.priceElement.textContent = tier ? tier.price : this.initialPrice;
      }

      if (this.totalElement) {
        this.totalElement.hidden = !tier;

        if (tier) {
          const total = this.formatter.format((tier.cents * count) / 100);
          const label = count === 1 && this.dataset.totalLabelOne ? this.dataset.totalLabelOne : this.dataset.totalLabel || '{total}';
          this.totalElement.textContent = label.replace('{total}', total).replace('{count}', String(count));
        }
      }
    }
  });
}
